import { Injectable } from '@angular/core';
import {
  Camera,
  CameraResultType,
  CameraSource,
  CameraOptions,
} from '@capacitor/camera';
import {
  ActionSheetController,
  LoadingController,
  ModalController,
} from '@ionic/angular';
import { APIService } from '../api.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import urljoin from 'url-join';
import { BehaviorSubject } from 'rxjs';
import { ProfileService } from './profile.service';
import { ImgCropperComponent } from 'src/app/components/image-cropper/image-cropper.component';

type AllowedMethod = 'post' | 'patch';
type ImageType = 'profile' | 'license';

@Injectable({
  providedIn: 'root',
})
export class ImageUploadService extends APIService {
  IMG_KEY = 'ProfileImage';
  currentPhotoType: ImageType;

  defaultImgUrl = 'assets/images/profile/placeholder.png';
  private profileImageUrl = new BehaviorSubject<string>(this.defaultImgUrl);
  private licenseImageUrl = new BehaviorSubject<string>('');
  currentPhoto = this.profileImageUrl.asObservable();
  licenseName = this.licenseImageUrl.asObservable();

  private licenseFormData = new BehaviorSubject<Blob>(new Blob());
  private hasStoredBlob = new BehaviorSubject<boolean>(false);
  license$ = this.licenseFormData.asObservable();
  hasStoredBlob$ = this.hasStoredBlob.asObservable();
  storedBlob: Blob;

  constructor(
    private profileService: ProfileService,
    private actionSheetController: ActionSheetController,
    private http: HttpClient,
    private loadingCtrl: LoadingController,
    private modalCtrl: ModalController
  ) {
    super();
  }

  showLoader() {
    this.loadingCtrl.create().then((loader) => loader.present());
  }

  hiderLoader() {
    this.loadingCtrl.dismiss();
  }

  changeProfilePhoto(photoUrl?: string) {
    Boolean(photoUrl)
      ? this.profileImageUrl.next(photoUrl)
      : this.profileImageUrl.next(this.defaultImgUrl);
  }

  generateLicensePhotoName(text: string) {
    const last15Char = text.substr(text.length - 15);
    this.licenseImageUrl.next(last15Char);
  }

  storeLicenseImageForUpload(blob: Blob) {
    const timeNow = new Date();
    const timeNowAsString = timeNow.toISOString().substring(0, 15);
    if (!(blob.size < 0)) {
      this.storedBlob = blob;
      this.hasStoredBlob.next(true);
      this.generateLicensePhotoName(timeNowAsString);
    }
  }

  updateStoredLicense(hasStoredLicense: boolean): void {
    this.hasStoredBlob.next(hasStoredLicense);
  }

  async selectImageSource(imageType?: ImageType) {
    this.currentPhotoType = imageType;
    const buttons = [
      {
        text: 'Take Photo',
        icon: 'camera',
        handler: () => {
          this.getPhoto(CameraSource.Camera, imageType);
        },
      },
      {
        text: 'Choose From Photos',
        icon: 'image',
        handler: () => {
          this.getPhoto(CameraSource.Photos, imageType);
        },
      },
    ];
    const actionSheet = await this.actionSheetController.create({
      header: 'Select Image Source',
      buttons,
    });
    await actionSheet.present();
  }

  async getPhoto(source: CameraSource, imageType?: ImageType) {
    const options: CameraOptions = {
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source,
    };
    const image = await Camera.getPhoto(options).then((photo) => {
      return photo.dataUrl;
    });

    await this.openCropperModal(image, imageType);
  }

  uploadOrStore(blobData: Blob, imageType: string) {
    if (imageType === 'profile') {
      const url = urljoin(this.BASE_URL, '/users/me/');
      this.uploadProfilePhoto(url, { image: blobData }, 'patch');
    } else if (imageType === 'license') {
      const url = urljoin(this.BASE_URL, '/users/me/profile/');
      this.uploadProfilePhoto(url, { license: blobData }, 'patch');
    } else {
      this.storeLicenseImageForUpload(blobData);
    }
  }

  dataURLtoBlob(dataURL: string) {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  async uploadStoredLicense() {
    const headers = new HttpHeaders();
    const fileMap = { license: this.storedBlob };
    const url = urljoin(this.BASE_URL, '/users/me/profile/');

    headers.append('Content-Type', 'multipart/form-data');
    headers.append('mimeType', 'multipart/form-data');

    const formData = new FormData();

    Object.keys(fileMap).forEach((key) => {
      const file = fileMap[key];
      if (file instanceof File) {
        formData.append(key, file, file.name);
      } else {
        formData.append(key, file, 'image.png');
      }
    });

    try {
      await this.http.patch(url, formData, { headers }).toPromise();
      this.updateStoredLicense(false);
    } catch (err) {
      this.updateStoredLicense(true);
    }
  }

  async uploadProfilePhoto(
    url: string,
    fileMap: { [key: string]: File | Blob },
    method: AllowedMethod = 'patch',
    params: { [key: string]: any } = null
  ) {
    this.showLoader();
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('mimeType', 'multipart/form-data');

    const formData = new FormData();

    Object.keys(fileMap).forEach((key) => {
      const file = fileMap[key];
      if (file instanceof File) {
        formData.append(key, file, file.name);
      } else {
        formData.append(key, file, 'image.png');
      }
    });

    Object.keys(params || {}).forEach((key) => {
      formData.append(key, params[key]);
    });

    try {
      await this.http[method](url, formData, { headers })
        .toPromise()
        .then((res: any) => {
          if (this.currentPhotoType === 'profile') {
            this.changeProfilePhoto(res.image.SQUARE);
            this.profileService.onUpdate$.next();
          } else {
            this.generateLicensePhotoName(res.license);
          }
        });
    } catch (err) {
      console.error(err);
    } finally {
      this.hiderLoader();
    }
  }

  async openCropperModal(imageToCrop: string, imageType?: ImageType) {
    const modal = await this.modalCtrl.create({
      component: ImgCropperComponent,
      componentProps: {
        imageToCrop,
      },
      backdropDismiss: false,
    });

    modal.onDidDismiss().then((data) => {
      if (data.data) {
        const b64image = data.data;
        const blobData: Blob = this.dataURLtoBlob(b64image);
        this.uploadOrStore(blobData, imageType);
      } else {
        return;
      }
    });
    return await modal.present();
  }
}
